import api from '../utils/api';

export interface MedicationItem {
  name: string;
  form?: string;
  strength?: string;
  quantity: number;
  instructions?: string;
  startDate?: string;
  endDate?: string;
}

export type LabelSize = 'DYMO_30252' | 'DYMO_30256' | 'DYMO_30334' | 'DYMO_30327';

export interface RxDispenseEvent {
  id: string;
  medicationId: string;
  clinicId: string;
  dispensedAt: string;
  dispensedById: string;
  dispensedBy?: { id: string; name: string; email: string };
  quantity: string; // Prisma Decimal serialises as string
  unit: string;
  labelSize?: LabelSize | null;
  invoiceLineId?: string | null;
  voidedAt?: string | null;
  voidedById?: string | null;
  voidReason?: string | null;
  notes?: string | null;
  /** Sig override printed on THIS fill's label. Null = use Medication.instructions. */
  instructionsOverride?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Medication {
  id: string;
  horseId: string;
  clinicId: string;
  recordId?: string;
  dischargeSummaryId?: string;
  name: string;
  dosage?: string;
  frequency?: string;
  route?: string;
  duration?: string;
  quantity?: number;
  instructions?: string;
  prescribedBy?: string;
  startDate: string;
  endDate?: string;
  notes?: string;
  deaSchedule?: string;
  // Rx refill fields
  isRx?: boolean;
  refillsAuthorized?: number;
  refillsRemaining?: number;
  expirationDate?: string | null;
  originalRxId?: string | null;
  isArchived: boolean;
  archivedAt?: string;
  archivedBy?: string;
  createdAt: string;
  updatedAt: string;
  horse?: any;
  clinic?: any;
  /**
   * Filtered count of non-voided RxDispenseEvent rows for this Medication.
   * Frontend uses this to compute "Fills X of Y" displays and to keep the
   * Dispense button enabled until the initial fill happens (Model B —
   * initial fill doesn't decrement refillsRemaining).
   */
  _count?: { dispenseEvents: number };
}

export interface AddMedicationsRequest {
  medications: MedicationItem[];
}

export const medicationService = {
  // Legacy method: Add medications via medical records
  async addMedications(recordId: string, medications: MedicationItem[]) {
    const response = await api.post(`/medical-records/${recordId}/medications`, {
      medications,
    });
    return response.data;
  },

  // Create a standalone medication
  async create(data: {
    horseId: string;
    name: string;
    dosage?: string;
    frequency?: string;
    route?: string;
    duration?: string;
    quantity?: number;
    instructions?: string;
    prescribedBy?: string;
    startDate: Date | string;
    endDate?: Date | string;
    notes?: string;
    deaSchedule?: string;
    // Per-unit price for billing — when set, the Rx auto-bill line item uses
    // unitPrice × dispensed quantity instead of defaulting to $0.
    unitPrice?: number;
    // Rx fields — when isRx=true the medication becomes a take-home Rx
    // dispensable via /medications/:id/dispense
    isRx?: boolean;
    refillsAuthorized?: number;
    expirationDate?: Date | string;
    /** Stock location the dispense deducts from; omit = server infers (truck → warehouse, ADR-0013). */
    dispenseLocationId?: string;
    /** Bottle (ControlledSubstanceLot.id) for the DEA auto-log; omit = server FIFO auto-pick (ADR-0014). */
    deaLotId?: string;
  }): Promise<Medication> {
    const response = await api.post('/medications', data);
    return response.data;
  },

  /**
   * Dispense one fill of a take-home Rx. Server creates an RxDispenseEvent
   * and decrements refillsRemaining transactionally. Returns the new event +
   * the updated medication record. Throws on:
   *   - 404 if medication doesn't exist in clinic
   *   - 400 if medication isn't an Rx, is archived, or schedule II
   *   - 403 if no refills remaining or Rx expired
   */
  async dispense(
    medicationId: string,
    data: {
      quantity: number;
      unit: string;
      notes?: string;
      labelSize?: LabelSize;
      /** Per-fill sig override; leave undefined to use Medication.instructions. */
      instructionsOverride?: string;
      /** Stock location this fill deducts from; omit = server infers (truck → warehouse, ADR-0013). */
      dispenseLocationId?: string;
      /** Bottle (ControlledSubstanceLot.id) for the DEA auto-log; omit = server FIFO auto-pick (ADR-0014). */
      deaLotId?: string;
    },
  ): Promise<{ event: RxDispenseEvent; medication: Medication }> {
    const response = await api.post(`/medications/${medicationId}/dispense`, data);
    return response.data;
  },

  /** All dispense events for one Rx, newest first. */
  async listDispenseEvents(medicationId: string): Promise<RxDispenseEvent[]> {
    const response = await api.get(`/medications/${medicationId}/dispense-events`);
    return response.data;
  },

  /**
   * Void a dispense event (soft-delete). Frees the fill back up — the backend
   * reconciles the Rx's refillsRemaining counter from the non-voided event
   * count AND reverses the auto-billed invoice line (when the invoice is still
   * pending), all in one transaction. Returns the voided event, the updated
   * medication, and a summary of the invoice reversal.
   */
  async voidDispenseEvent(
    medicationId: string,
    eventId: string,
    reason?: string,
  ): Promise<{
    event: RxDispenseEvent;
    medication: Medication;
    invoiceReversal: { reversed: boolean; reason?: string; amount?: number };
  }> {
    const response = await api.post(
      `/medications/${medicationId}/dispense-events/${eventId}/void`,
      { reason },
    );
    return response.data;
  },

  // Get all medications with optional filters
  async getAll(filters?: {
    horseId?: string;
    startDate?: string;
    endDate?: string;
    includeArchived?: boolean;
  }): Promise<Medication[]> {
    const response = await api.get('/medications', { params: filters });
    return response.data;
  },

  // Get a single medication
  async getOne(id: string): Promise<Medication> {
    const response = await api.get(`/medications/${id}`);
    return response.data;
  },

  // Update a medication
  async update(id: string, data: {
    name?: string;
    dosage?: string;
    frequency?: string;
    route?: string;
    duration?: string;
    quantity?: number;
    instructions?: string;
    prescribedBy?: string;
    startDate?: Date | string;
    endDate?: Date | string;
    notes?: string;
  }): Promise<Medication> {
    const response = await api.put(`/medications/${id}`, data);
    return response.data;
  },

  // Archive a medication
  async archive(id: string): Promise<Medication> {
    const response = await api.post(`/medications/${id}/archive`);
    return response.data;
  },

  // Restore an archived medication
  async restore(id: string): Promise<Medication> {
    const response = await api.post(`/medications/${id}/restore`);
    return response.data;
  },

  // Delete a medication
  async delete(id: string): Promise<void> {
    await api.delete(`/medications/${id}`);
  },
};

